import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, money, useList, type Column, type Row } from '../components/DataTable';
import Modal from '../components/Modal';
// 「청구」는 미수금·거래처별 정산과 **같은 폼·같은 저장**(saveResultMisu — 줄이 없으면 새로 만든다).
import MisuEditForm, { misuAccess } from '../components/MisuEditForm';
import { useMe } from '../lib/me';
import { useAutoSearch } from '../lib/search';
import DriveMonPicker from '../components/DriveMonPicker';
import PayOnlyPanel from '../components/PayOnlyPanel';
import BillGroupPanel from '../components/BillGroupPanel';

/**
 * 미청구 — 미수금 탭의 두 번째 보기(2026-10-02 way · 회계팀 확인용).
 *
 * 미수금 목록은 `result_misu` **줄이 있는 건만** 읽어서, 거래처별 정산엔 있는데 청구를 아직 안 건
 * 접수·달(예: 6440 한국외대 통근 2026-09·10)이 어디에도 안 보였다. 이 보기가 그 빈칸이다.
 *
 * 🔴 축은 **정산월**이다(미수금 목록은 운행시작일). 「청구」는 달마다 거는 것이라 정산월 한 달을 고른다.
 *    화면에도 그 사실을 적는다 — 안 적으면 「미수금 목록과 건수가 왜 다르지」에서 멈춘다.
 * 🔴 「공제 대기」 칸 = 그 달 영업정산 기사 지급·공제 줄. 청구(청구대상 = 영업 청구 요청 · 청구완료 또는 입금)가 없으면
 *    **명세에 안 들어가고 OPS 선지급 게이트도 막힌다**(salaryGen.ts GATES). OPS 줄은 돈이 이미 나간 것이다.
 * 서버 판정·0원 제외 규칙은 `functions/src/settle.ts` UNBILLED_SQL 머리 주석이 정본.
 */

/** 거래처별 정산과 같이 이번 달(KST)을 얹는다 — 통근 줄은 그 달을 열 때 생기므로 데이터가 아직 없어도 고를 수 있게. */
function withThisMonth(months: readonly string[]): string[] {
  const kst = new Date(Date.now() + 9 * 3600_000);
  const cur = `${kst.getUTCFullYear()}-${String(kst.getUTCMonth() + 1).padStart(2, '0')}`;
  return [...new Set([cur, ...months])].sort().reverse();
}

export default function SettleUnbilled({
  months,
  onOpenCust,
  toggle,
}: {
  months: readonly string[];
  /** 거래처별 정산 탭으로 그 접수·달을 연다(부모 `Settle` 이 탭·정산월·검색어를 바꾼다). */
  onOpenCust?: (recvId: number, driveMon: string) => void;
  /** 미수/미청구 보기 전환(부모 `SettleMisu`) — 필터와 **한 줄**에 둔다. */
  toggle?: React.ReactNode;
}) {
  const { me } = useMe();
  /** 청구·입금 상태 = 회계 화면 입력·관리자(서버 saveResultMisu scope 'misu'). 그 밖은 보기만. */
  const canEdit = misuAccess(me, 'misu');
  const monList = useMemo(() => withThisMonth(months), [months]);

  const [mon, setMon] = useState('');
  // 월 목록이 늦게 오면 그때 가장 최근 달로 시작한다(이번 달 = 아직 청구 전인 달이 가장 많다).
  useEffect(() => {
    if (!mon && monList[0]) setMon(monList[0]);
  }, [mon, monList]);

  const [includeZero, setIncludeZero] = useState(false);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('settle-unbilled', 50);
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  const [nonce, setNonce] = useState(0);
  const [edit, setEdit] = useState<Row | null>(null);
  const [hiddenZero, setHiddenZero] = useState(0);

  const { rows, loading, error, total } = useList(
    async () => {
      if (!mon) return { rows: [] as Row[] };
      const r = await api.listResultUnbilled({
        driveMon: mon, includeZero, q: q || undefined, limit: PAGE, offset: page * PAGE,
      });
      setHiddenZero(r.hiddenZero);
      return r as unknown as { rows: Row[] };
    },
    [mon, includeZero, q, page, PAGE, nonce],
  );

  const columns: Column[] = [
    { key: 'recv_id', label: '접수 NO', align: 'right', phone: 'hide', render: (r) => <span className="text-xs text-ink-soft">{dash(r.recv_id)}</span> },
    { key: 'cust_name', label: '거래처', phone: 'pin' },
    {
      key: 'recv_type',
      label: '구분',
      render: (r) => (
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${r.recv_type === '통근' ? 'bg-violet-50 text-violet-700' : 'bg-canvas text-ink-soft'}`}>
          {dash(r.recv_type)}
        </span>
      ),
    },
    { key: 'drive_mon', label: '정산월' },
    {
      key: 'cost_cust_total',
      label: '청구액(수입계)',
      align: 'right',
      render: (r) => <b>{money(r.cost_cust_total)}</b>,
    },
    {
      key: 'cur_status',
      label: '현재 상태',
      // 없음 = 원장 줄 자체가 없다 · 청구대상 = 계산서 전 후보(게이트는 아직 닫혀 있다).
      render: (r) => (
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${r.cur_status === '없음' ? 'bg-amber-50 text-amber-800' : 'bg-canvas text-ink-soft'}`}>
          {dash(r.cur_status)}
        </span>
      ),
    },
    {
      key: 'held_cnt',
      label: '공제 대기',
      totalRender: (t) => <span>{Number(t.held_cnt ?? 0)}줄 · OPS {Number(t.ops_cnt ?? 0)}</span>,
      render: (r) => {
        const held = Number(r.held_cnt ?? 0);
        const ops = Number(r.ops_cnt ?? 0);
        if (held === 0 && ops === 0) return <span className="text-ink-faint">—</span>;
        return (
          <span
            className="whitespace-nowrap rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700"
            title={`그 달 영업정산 기사 공제 ${money(r.held_minus)} · 지급 ${money(r.held_plus)} — 청구 전이라 명세·선지급 게이트에 막혀 있습니다`}
          >
            {ops > 0 && <>OPS {ops} · </>}
            영업정산 {held}줄
          </span>
        );
      },
    },
    { key: 'fin_req_method', label: '청구방법', phone: 'hide' },
    { key: 'fin_manager', label: '거래처정산담당자', phone: 'hide' },
    {
      key: 'act',
      label: '',
      render: (r) => (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={(e) => { e.stopPropagation(); setEdit(r); }}>
            {canEdit ? '청구' : '청구 정보'}
          </button>
          {onOpenCust && (
            <button
              type="button"
              className="btn-ghost px-2 py-0.5 text-xs"
              title="거래처별 정산에서 이 접수·정산월 상세를 엽니다"
              onClick={(e) => { e.stopPropagation(); onOpenCust(Number(r.recv_id), String(r.drive_mon)); }}
            >
              정산 보기 →
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      {/* 위칸 한 줄(2026-10-02 way) — 설명은 보기 전환 옆 ⓘ(SettleMisu UNBILLED_TIP). */}
      <form
        onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }}
        className="flex flex-wrap items-center gap-x-2 gap-y-1.5"
      >
        {toggle}
        {/* DriveMonPicker 는 자기 label 을 그린다 — <label> 로 감싸지 말 것(SettleMisu 주석 참고). */}
        <DriveMonPicker months={monList} value={mon} width="w-28" onChange={(m) => { setMon(m); setPage(0); }} />
        <span className="flex min-w-0 flex-1 items-center gap-2 md:flex-none">
          <input
            className="field min-w-0 flex-1 md:w-52 md:flex-none"
            placeholder="거래처 · 담당자 이름 · 청구방법 · 접수 NO"
            title="거래처 · 거래처정산담당자 · 청구방법 · 접수 NO"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </span>
        <label
          className="flex items-center gap-1 whitespace-nowrap text-xs text-ink-soft"
          title={hiddenZero > 0 && !includeZero ? `수입계·지출계가 0원이고 영업정산 줄도 없는 ${hiddenZero.toLocaleString('ko-KR')}건은 뺐습니다(비용 노선·테스트 접수 등).` : '수입계·지출계 0원 · 영업정산 줄 없는 접수'}
        >
          <input type="checkbox" checked={includeZero} onChange={(e) => { setIncludeZero(e.target.checked); setPage(0); }} />
          0원 줄{!includeZero && hiddenZero > 0 ? ` (${hiddenZero.toLocaleString('ko-KR')}건 숨김)` : ''}
        </label>
        {!canEdit && <span className="whitespace-nowrap text-xs text-ink-faint" title="청구·입금 상태 입력은 회계 입력 권한이 필요합니다">보기 전용</span>}
      </form>
      {/* 검색 <form> 밖 — 창(Modal portal) 안 submit 이 검색으로 버블하지 않게. */}
      {mon && <div className="-mt-1 flex justify-end gap-1"><BillGroupPanel mon={mon} onChanged={() => setNonce((n) => n + 1)} /><PayOnlyPanel mon={mon} onChanged={() => setNonce((n) => n + 1)} /></div>}

      {error && <ErrorBox message={error} />}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        totalAt="top"
        tableId="settle.unbilled"
        onRowClick={setEdit}
        activeRow={(r) => edit !== null && edit.recv_id === r.recv_id && edit.drive_mon === r.drive_mon}
      />
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />

      {edit && (
        <Modal>
          <MisuEditForm
            row={edit}
            scope="misu"
            onClose={() => setEdit(null)}
            onSaved={() => { setEdit(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}
    </div>
  );
}
